import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { adminApi } from "../api/client";
import type { AdminAccount } from "../types/api";

const TOKEN_KEY = "pulsepass_admin_token";
const ADMIN_KEY = "pulsepass_admin_profile";

type AdminAuthContextValue = {
  admin: AdminAccount | null;
  token: string | null;
  isBootstrapping: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
};

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

export function AdminAuthProvider({ children }: PropsWithChildren) {
  const [admin, setAdmin] = useState<AdminAccount | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const storedToken = window.localStorage.getItem(TOKEN_KEY);
    const storedAdmin = window.localStorage.getItem(ADMIN_KEY);

    if (!storedToken) {
      setIsBootstrapping(false);
      return;
    }

    setToken(storedToken);
    if (storedAdmin) {
      try {
        setAdmin(JSON.parse(storedAdmin) as AdminAccount);
        setIsBootstrapping(false);
      } catch {
        window.localStorage.removeItem(ADMIN_KEY);
      }
    }

    void adminApi
      .me(storedToken)
      .then(({ admin: currentAdmin }) => {
        setAdmin(currentAdmin);
        window.localStorage.setItem(ADMIN_KEY, JSON.stringify(currentAdmin));
      })
      .catch(() => {
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(ADMIN_KEY);
        setToken(null);
        setAdmin(null);
      })
      .finally(() => setIsBootstrapping(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    const response = await adminApi.login({ email, password });
    setToken(response.token);
    setAdmin(response.admin);
    window.localStorage.setItem(TOKEN_KEY, response.token);
    window.localStorage.setItem(ADMIN_KEY, JSON.stringify(response.admin));
  };

  const signOut = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(ADMIN_KEY);
    setToken(null);
    setAdmin(null);
  };

  const value = useMemo(
    () => ({
      admin,
      token,
      isBootstrapping,
      signIn,
      signOut,
    }),
    [admin, token, isBootstrapping]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
}
